import * as reduxPersist from 'redux-persist';
import localForage from 'localforage';
import createEncryptor from 'redux-persist-transform-encrypt';
import CryptoJS from 'crypto-js';
import { convertToJSRecursive, convertToImmutableRecursive } from 'utils/immutableSerialize';
import * as localPreferences from 'utils/localPreferences';
import store from 'store';
import createReducer from 'reducers';
import { getDatasetId } from 'utils/auth/selectors';
import { getLogger } from 'utils/logger';
import { authLogout } from 'utils/auth/actions';
import tracker from 'utils/tracker';
import * as sessionStorageEx from 'utils/sessionStorageEx';
import { persistDataLoadComplete, hardReset } from 'data/actions';
import { updateAllFlags } from 'utils/featureFlags/initialize';

import { REDUCER_KEY as ACCOUNTS_REDUCER_KEY } from 'data/accounts/reducer';
import { REDUCER_KEY as CATEGORIES_REDUCER_KEY } from 'data/categories/reducer';
import { REDUCER_KEY as DATASETS_REDUCER_KEY } from 'data/datasets/reducer';
import { REDUCER_KEY as DOCUMENTS_REDUCER_KEY } from 'data/documents/reducer';
import { REDUCER_KEY as FILTER_REDUCER_KEY } from 'data/filters/reducer';
import { REDUCER_KEY as INSTITUTIONS_REDUCER_KEY } from 'data/institutions/reducer';
import { REDUCER_KEY as INSTITUTION_LOGINS_REDUCER_KEY } from 'data/institutionLogins/reducer';
import { INVESTMENT_HOLDINGS_V2_REDUCER_KEY } from 'data/investmentHoldingsV2/investmentHoldingsV2Reducer';
import { REDUCER_KEY as MEMORIZED_RULES_REDUCER_KEY } from 'data/memorizedRules/reducer';
import { REDUCER_KEY as PROFILE_REDUCER_KEY } from 'data/profile/reducer';
import { REDUCER_KEY as RULES_REDUCER_KEY } from 'data/renameRules/reducer';
import { REDUCER_KEY as SPENDING_WATCH_LIST_REDUCER_KEY } from 'data/spendingWatchList/reducer';
import { REDUCER_KEY as TAGS_REDUCER_KEY } from 'data/tags/reducer';
import { REDUCER_KEY as TRANSACTIONS_REDUCER_KEY } from 'data/transactions/reducer';
import { REDUCER_KEY as PAYEES_REDUCER_KEY } from 'data/payees/reducer';
import { SCHEDULED_TRANSACTIONS_REDUCER_KEY } from 'data/scheduledTransactions/scheduledTransactionsReducer';
import { FREE_TO_SPEND_REDUCER_KEY } from 'data/freeToSpend/freeToSpendReducer';
import { INVESTMENT_QUOTES_V2_REDUCER_KEY } from 'data/investmentQuotesV2/investmentQuotesV2Reducer';
import { REDUCER_KEY as GOALS_REDUCER_KEY } from 'data/goals/reducer';
import { REDUCER_KEY as AUTH_REDUCER_KEY } from 'utils/auth/reducer';
import { ENTITLEMENTS_REDUCER_KEY } from 'data/entitlements/entitlementsTypes';
import { ALERT_RULES_REDUCER_KEY } from 'data/alertRules/reducer';
import { REDUCER_KEY as FEATURE_FLAGS_REDUCER_KEY } from 'utils/featureFlags/types';
import { POSTPONED_ACTIONS_REDUCER_KEY } from 'data/postponedActions/postponedActionsReducer';
import { BUDGETS_REDUCER_KEY } from 'data/budgets/budgetsReducer';
import { BUDGET_ITEMS_REDUCER_KEY } from 'data/budgetItems/budgetItemsReducer';
import { CATEGORY_GROUP_LISTS_REDUCER_KEY } from 'data/categoryGroupLists/categoryGroupListsReducer';
import { CATEGORY_GROUPS_REDUCER_KEY } from 'data/categoryGroups/categoryGroupsReducer';
import { INVESTMENT_HOLDINGS_REDUCER_KEY } from 'data/investmentHoldings/reducer';
import { INVESTMENT_BALANCES_V2_REDUCER_KEY } from 'data/investmentBalancesV2/investmentBalancesV2Reducer';
import { INVESTMENT_PERFORMANCE_V2_REDUCER_KEY } from 'data/investmentPerformanceV2/investmentPerformanceV2Reducer';
import { INVESTMENT_SYMBOLS_KEY } from 'data/investmentSymbols/investmentSymbolsReducer';
import { REDUCER_KEY as PREFERENCES_REDUCER_KEY } from 'data/preferences/reducer';
import { ALERTS_REDUCER_KEY } from 'data/alerts/alertsReducer';

const log = getLogger('persistor.js');

let persistor;

const persistConfig = {
  key: 'root', // key is overriden in the code below
  storage: localForage,
  version: 3, // increment it if existing data model has breaking changes
  transforms: [
    reduxPersist.createTransform( // reset temporary data
      (inboundState) => inboundState,
      (outboundState) => {
        let newReducerState = outboundState;
        if (newReducerState.isLoading && newReducerState.set) { // reset isLoading
          newReducerState = newReducerState.set('isLoading', false);
        }
        if (newReducerState.resourcesById && newReducerState.resourcesById.set && newReducerState.resourcesById.filter) { // remove optimistic/ghost records
          newReducerState = newReducerState.set('resourcesById', newReducerState.resourcesById.filter((resource, key) => resource.id === key));
        }
        return newReducerState;
      },
    ),
    reduxPersist.createTransform(
      (inboundState) => convertToJSRecursive(inboundState),
      (outboundState) => convertToImmutableRecursive(outboundState),
    ),
  ],
  whitelist: [
    FEATURE_FLAGS_REDUCER_KEY,
    AUTH_REDUCER_KEY,
    ENTITLEMENTS_REDUCER_KEY,
    ACCOUNTS_REDUCER_KEY,
    CATEGORIES_REDUCER_KEY,
    DATASETS_REDUCER_KEY,
    DOCUMENTS_REDUCER_KEY,
    FILTER_REDUCER_KEY,
    INSTITUTIONS_REDUCER_KEY,
    INSTITUTION_LOGINS_REDUCER_KEY,
    INVESTMENT_HOLDINGS_V2_REDUCER_KEY,
    MEMORIZED_RULES_REDUCER_KEY,
    PROFILE_REDUCER_KEY,
    RULES_REDUCER_KEY,
    SPENDING_WATCH_LIST_REDUCER_KEY,
    TAGS_REDUCER_KEY,
    TRANSACTIONS_REDUCER_KEY,
    PAYEES_REDUCER_KEY,
    SCHEDULED_TRANSACTIONS_REDUCER_KEY,
    FREE_TO_SPEND_REDUCER_KEY,
    INVESTMENT_QUOTES_V2_REDUCER_KEY,
    GOALS_REDUCER_KEY,
    ALERT_RULES_REDUCER_KEY,
    ALERTS_REDUCER_KEY,
    POSTPONED_ACTIONS_REDUCER_KEY,
    BUDGETS_REDUCER_KEY,
    BUDGET_ITEMS_REDUCER_KEY,
    CATEGORY_GROUP_LISTS_REDUCER_KEY,
    CATEGORY_GROUPS_REDUCER_KEY,
    INVESTMENT_HOLDINGS_REDUCER_KEY,
    PREFERENCES_REDUCER_KEY,
    INVESTMENT_BALANCES_V2_REDUCER_KEY,
    INVESTMENT_PERFORMANCE_V2_REDUCER_KEY,
    INVESTMENT_SYMBOLS_KEY,
  ],
  migrate: (restoredState, toVersion) => {
    const fromVersion = restoredState?._persist?.version; // eslint-disable-line no-underscore-dangle
    if (fromVersion !== toVersion) {
      log.warn(`version mismatch - reset to defaults (${fromVersion} -> ${toVersion})`);
      persistor?.purge?.();
      return Promise.resolve({});
    }
    return Promise.resolve(restoredState);
  },
  throttle: 50,
  // debug: true,
  serialize: (data) => {
    log.info(`save to persist (${typeof data})...`);
    return JSON.stringify(data);
  },
  deserialize: (serial) => {
    log.info(`load from persist (${typeof serial})...`);
    return JSON.parse(serial);
  },
};

export const persistStore = (inStore = undefined, inBaseReducer = undefined, inPersistKey = undefined) => {
  persistor = undefined;
  const persist = localPreferences.getPersist();
  if (persist) {
    const lastSession = sessionStorageEx.getAuthSession() || localPreferences.getLastSession();
    const theStore = inStore || store;
    const datasetId = getDatasetId(theStore.getState()) || lastSession?.datasetId;
    const baseReducer = inBaseReducer || createReducer();
    let key = inPersistKey;
    if (!key && datasetId) {
      key = CryptoJS.SHA256(datasetId);
    }
    if (key && datasetId) {
      const encryptor = createEncryptor({
        secretKey: datasetId,
        onError: (error) => {
          log.warn(`encryptor error - reset to defaults: ${error}`);
          assert(false, `encryptor error: ${error}`);
          persistor?.purge?.();
        },
      });
      if (encryptor) {
        const configUpdated = {
          ...persistConfig,
          key,
          transforms: [
            ...(persistConfig.transforms || []),
            encryptor,
          ],
        };
        const persistReducer = reduxPersist.persistReducer(configUpdated, baseReducer);

        const persistReducerProxy = (state, action, ...other) => {
          if (action.type === authLogout.toString()) {
            reduxPersist.purgeStoredState(configUpdated);
          } else if (action.type === hardReset.toString()) {
            reduxPersist.purgeStoredState(configUpdated);
          }
          return persistReducer(state, action, ...other);
        }
        theStore.replaceReducer(persistReducerProxy);

        tracker.track(tracker.events.persistLoadStart);
        tracker.timer(tracker.events.persistLoadComplete);
        const start = new Date();

        persistor = reduxPersist.persistStore(
          theStore,
          null,
          () => {
            const complete = new Date();
            log.info(`load from persist complete in ${complete - start}ms...`);
            tracker.track(tracker.events.persistLoadComplete);

            theStore.dispatch(persistDataLoadComplete());
            updateAllFlags();
          },
        );
      }
    }
  }
  return persistor;
};

export const getPersistor = () => persistor;
